import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import {
  displayWeight,
  displayLength,
  inputWeightToKg,
  inputLengthToCm,
} from '../lib/units'

const STORAGE_KEY = 'routine-app-units'

const UnitsContext = createContext()

export const useUnits = () => {
  const context = useContext(UnitsContext)
  if (!context) {
    throw new Error('useUnits must be used within a UnitsProvider')
  }
  return context
}

export const UnitsProvider = ({ children }) => {
  const [unitSystem, setUnitSystem] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'imperial' ? 'imperial' : 'metric'
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, unitSystem)
  }, [unitSystem])

  const value = useMemo(() => {
    const isImperial = unitSystem === 'imperial'
    const weightUnit = isImperial ? 'lb' : 'kg'
    const lengthUnit = isImperial ? 'in' : 'cm'

    return {
      unitSystem,
      isImperial,
      weightUnit,
      lengthUnit,
      setUnitSystem,
      toggleUnits: () => setUnitSystem((s) => (s === 'metric' ? 'imperial' : 'metric')),

      // Display helpers — return formatted strings
      formatWeight: (kg) => {
        if (kg == null) return '—'
        const v = displayWeight(kg, unitSystem)
        return v.toFixed(2)
      },
      formatLength: (cm) => {
        if (cm == null) return '—'
        const v = displayLength(cm, unitSystem)
        return v.toFixed(1)
      },

      // Raw display values (for chart axes, etc.)
      displayValueWeight: (kg) => displayWeight(kg, unitSystem),
      displayValueLength: (cm) => displayLength(cm, unitSystem),

      // Input → metric storage
      weightToKg: (value) => inputWeightToKg(value, unitSystem),
      lengthToCm: (value) => inputLengthToCm(value, unitSystem),
    }
  }, [unitSystem])

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}
